import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Linkedin } from 'lucide-react';
import { business } from '@/config/business';

const columns = [
  {
    title: 'Services',
    links: [
      { label: 'Matterport 3D Tours', to: '/matterport-services-nashville-tn' },
      { label: 'Insurance Documentation', to: '/insurance-documentation-nashville-tn' },
      { label: 'Construction Documentation', to: '/construction-documentation-nashville-tn' },
      { label: 'Floor Plans & Measurements', to: '/real-estate-3d-tours-nashville-tn' },
      { label: 'Digital Twins', to: '/digital-twin-solution' },
      { label: 'Evidence & Claims Capture', to: '/legal-insurance' },
    ],
  },
  {
    title: 'Industries',
    links: [
      { label: 'Real Estate', to: '/residential-services' },
      { label: 'Insurance & Restoration', to: '/legal-insurance' },
      { label: 'Construction', to: '/construction-3d-services' },
      { label: 'Hospitality', to: '/hospitality' },
      { label: 'Commercial', to: '/commercial-real-estate' },
      { label: 'All industries', to: '/industries' },
    ],
  },
  {
    title: 'Coverage',
    links: [
      { label: 'Nashville, TN', to: '/service-areas' },
      { label: 'Franklin, TN', to: '/service-area/franklin-tn' },
      { label: 'Brentwood, TN', to: '/service-area/brentwood-tn' },
      { label: 'Murfreesboro, TN', to: '/service-area/murfreesboro-tn' },
      { label: 'Cleveland Heights, OH', to: '/service-area/cleveland-heights-oh' },
      { label: 'Akron, OH', to: '/service-area/akron-oh' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', to: '/about' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Portfolio', to: '/portfolio' },
      { label: 'Social Proof', to: '/social-proof' },
      { label: 'Blog', to: '/blog' },
      { label: 'Contact', to: '/contact' },
    ],
  },
];

const Footer = () => {
  return (
    <footer className="bg-ink text-background">
      {/* CTA band */}
      <div className="border-b border-white/15 bg-primary px-5 py-10 md:px-8 lg:px-12">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="font-mono text-[0.64rem] font-bold uppercase tracking-[0.13em] text-accent">
              Next step
            </p>
            <h2 className="ed-display mt-3 max-w-xl text-4xl text-primary-foreground md:text-5xl">
              Scope, price and schedule in one call.
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex min-h-[52px] items-center bg-background px-6 text-[0.7rem] font-extrabold uppercase tracking-[0.09em] text-primary"
            >
              Get a free quote
            </Link>
            <a
              href={business.phone.href}
              className="inline-flex min-h-[52px] items-center gap-2 border border-background/60 px-6 text-[0.7rem] font-extrabold uppercase tracking-[0.09em] text-primary-foreground"
            >
              <Phone className="h-4 w-4" />
              {business.phone.display}
            </a>
          </div>
        </div>
      </div>

      <div className="px-5 py-16 md:px-8 lg:px-12">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_repeat(4,1fr)]">
          <div>
            <img
              src="/lovable-uploads/c89aca65-692c-4285-8896-b34e66ebbdab.png"
              alt="TourMy.co Logo"
              className="mb-5 h-8 w-auto brightness-0 invert"
              width="32"
              height="32"
              loading="lazy"
            />
            <p className="max-w-xs text-sm leading-relaxed text-white/70">
              Matterport Pro3 capture, measured floor plans and documentation for real estate,
              construction and insurance teams in Middle Tennessee and Northeast Ohio.
            </p>
            <div className="mt-6 flex gap-4">
              <a href={business.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="TourMy on Facebook">
                <Facebook aria-hidden="true" className="h-5 w-5 text-white/60 hover:text-accent" />
              </a>
              <a href={business.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="TourMy on LinkedIn">
                <Linkedin aria-hidden="true" className="h-5 w-5 text-white/60 hover:text-accent" />
              </a>
            </div>
          </div>

          {columns.map((column) => (
            <div key={column.title}>
              <h3 className="font-mono text-[0.62rem] font-bold uppercase tracking-[0.13em] text-accent">
                {column.title}
              </h3>
              <ul className="mt-5 space-y-2.5 text-sm text-white/70">
                {column.links.map((link) => (
                  <li key={link.label + link.to}>
                    <Link to={link.to} className="hover:text-background">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 grid gap-6 border-t border-white/15 pt-8 text-sm text-white/70 md:grid-cols-3">
          <a href={business.phone.href} className="flex items-center gap-2 hover:text-background">
            <Phone className="h-4 w-4 text-accent" />
            {business.phone.display}
          </a>
          <a href={business.emailHref} className="flex items-center gap-2 hover:text-background">
            <Mail className="h-4 w-4 text-accent" />
            {business.email}
          </a>
          <span className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-accent" />
            Nashville, TN &middot; Cleveland, OH
          </span>
        </div>

        <p className="mt-8 font-mono text-[0.62rem] uppercase tracking-[0.12em] text-white/50">
          &copy; {business.copyrightYear} {business.name} — Digital spatial intelligence
        </p>
      </div>
    </footer>
  );
};

export default Footer;
